import type { JSX, ReactNode } from 'react';
import { cn } from '../../lib/cn.js';
import { LIBELLE_LIEN, MarqueDeLien } from '../../lib/liens.js';

/* ------------------------------------------------------------------ */
/* Le titre d'une carte de fiche                                       */
/* ------------------------------------------------------------------ */

/**
 * LE TITRE D'UNE CARTE DE FICHE : le mot, souligne d'un filet.
 *
 * Il vit SEUL dans ce module, sans dependance lourde : la fiche, la carte liee
 * ET la carte des fichiers joints (rendue par l'hote, qui le prend au barrel de
 * `@taki/ui`) portent donc le meme titre, au pixel pres : le filet du titre est
 * une regle de fiche (cascade fiche-5 du lot design), pas une decoration de
 * carte de champs, et deux copies de ses classes auraient diverge au premier
 * ajustement.
 *
 * `aside` est le seul ecart : la carte liee epingle son compte serveur a droite
 * du filet. Une carte de champs n'en passe pas et rend exactement ce qu'elle
 * rendait avant.
 *
 * `serre` EST LE SECOND ECART, et il ne concerne lui aussi que la carte LIEE,
 * une vue sur deux : sous le filet, une LISTE apporte deja son propre blanc
 * (une rangee fait 3.5rem pour deux lignes de texte centrees), donc l'ecart du
 * filet s'ajoute a celui de la rangee. Un TABLEAU n'apporte rien : il vit dans
 * une boite bornee dont le cadre commence au ras de son en-tete, et serre, le
 * filet se collerait a ce cadre, deux traits a un cheveu l'un de l'autre. Une
 * carte de CHAMPS n'a rien non plus : son premier champ commence au ras de sa
 * boite. Ces deux-la prennent l'ecart entier. Le filet reste le meme au pixel
 * pres ; c'est ce qu'il y a SOUS lui qui differe. Une carte qui se rend en UNE
 * LIGNE D'ETAT (`CardEmptyState`) est serree aussi : l'etat porte son propre
 * blanc (`py-3`), le filet ne s'y ajoute pas.
 *
 * `lien` EST LE TROISIEME, et il ne concerne que la carte LIEE : son titre annonce
 * les fiches d'une AUTRE porte, c'est-a-dire l'autre sens du lien que porte une
 * colonne de reference. Le meme signe des deux cotes est tout l'interet :
 * « Materiel » sur la ligne d'une intervention et « Interventions » sur la
 * fiche d'un materiel sont un seul et meme lien, lu dans ses deux sens. Une
 * carte de CHAMPS ne le passe pas : elle ne montre que la fiche ouverte.
 */
export function CardHeading({
  title,
  aside,
  lien = false,
  serre = false,
}: {
  title: string;
  aside?: ReactNode;
  lien?: boolean;
  serre?: boolean;
}): JSX.Element {
  const titre = (
    <h3 className="min-w-0 truncate text-base font-semibold leading-[1.3] tracking-[-0.01em] text-ink-900">
      {title}
    </h3>
  );
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 border-b border-border pb-2',
        /* Aucun ecart : la rangee qui suit porte tout le blanc a elle seule.
           Classes ecrites en toutes lettres, Tailwind ne genere que ce qu'il lit. */
        serre ? 'mb-0' : 'mb-4',
      )}
    >
      {/* LE GROUPE N'EXISTE QUE POUR LA MARQUE, et il garde `min-w-0` : sans
          lui, le titre cesserait de se couper et pousserait le compte serveur
          hors du filet. La marque, elle, ne se comprime jamais (`shrink-0`). */}
      {lien ? (
        <span className="flex min-w-0 items-center gap-1.5">
          {titre}
          <MarqueDeLien titre={LIBELLE_LIEN.carte} size={14} />
        </span>
      ) : (
        titre
      )}
      {aside}
    </div>
  );
}

/**
 * L'ETAT D'UNE CARTE DE FICHE QUI N'A RIEN A MONTRER : UNE ligne a plat, sous
 * un titre `serre`. Brouillon, aucune fiche liee, aucun fichier joint, aucun
 * resultat : la carte liee, la carte des fichiers et leurs apercus disent leur
 * vide du meme pas. Un seul endroit pour sa police, sa couleur et son ecart au
 * filet, sinon deux cartes voisines se lisent a deux hauteurs et deux corps.
 *
 * `eteint` grise l'etat qui n'est pas un fait mais un « pas encore » ou un
 * « pas ici » (brouillon, port absent) ; `title` porte alors sa raison.
 */
export function CardEmptyState({
  children,
  eteint = false,
  title,
  role,
}: {
  children: ReactNode;
  eteint?: boolean;
  title?: string;
  role?: 'status';
}): JSX.Element {
  return (
    <p
      title={title}
      role={role}
      className={cn('px-1 py-3 text-[0.8125rem]', eteint ? 'text-ink-300' : 'text-ink-500')}
    >
      {children}
    </p>
  );
}
